import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, FORECASTLY_LOGO_LIGHT, PROFITMIND_LOGO } from "@/lib/brand";
import { ArrowRight } from "@phosphor-icons/react";

export default function ForgotPassword() {
  const [params] = useSearchParams();
  const tokenFromUrl = params.get("token");
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [linkSent, setLinkSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  // If reset token in URL, render reset form
  const [pw, setPw] = useState({ new_password: "", confirm: "" });

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setLinkSent(true);
      // dev_reset_link returned only while SMTP is not yet wired
      if (res.data?.dev_reset_link) setDevLink(res.data.dev_reset_link);
      toast.success("If that email is registered, a reset link has been sent.");
    } catch {
      toast.error("Could not send reset link. Try again.");
    } finally {
      setBusy(false);
    }
  };

  const doReset = async (e) => {
    e.preventDefault();
    if (pw.new_password !== pw.confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    if (pw.new_password.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: tokenFromUrl,
        new_password: pw.new_password,
      });
      toast.success("Password updated. You can sign in now.");
      nav("/auth", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-black/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-28">
          <a href={process.env.REACT_APP_MARKETING_URL || "https://forecastly.me"} className="flex items-center">
            <img
              src={FORECASTLY_LOGO_LIGHT}
              alt="forecastly"
              className="w-[250px] h-auto object-contain"
            />
          </a>
          <Link to="/auth" className="btn-secondary">
            Back to sign in
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6 md:p-12">
        <div className="w-full max-w-md card">
          {tokenFromUrl ? (
            <>
              <div className="section-eyebrow">Set a new password</div>
              <h1 className="font-heading uppercase text-3xl tracking-wide mt-4">
                Choose your new password
              </h1>
              <form onSubmit={doReset} className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1">New password</label>
                  <input
                    type="password"
                    className="input"
                    value={pw.new_password}
                    onChange={(e) => setPw({ ...pw, new_password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="reset-new-pw"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold mb-1">Confirm new password</label>
                  <input
                    type="password"
                    className="input"
                    value={pw.confirm}
                    onChange={(e) => setPw({ ...pw, confirm: e.target.value })}
                    required
                    data-testid="reset-confirm-pw"
                  />
                </div>
                <button className="btn-primary w-full justify-center" disabled={busy} data-testid="reset-submit-btn">
                  Update password <ArrowRight size={16} weight="bold" />
                </button>
              </form>
            </>
          ) : (
            <>
              <div className="section-eyebrow">Forgot password</div>
              <h1 className="font-heading uppercase text-3xl tracking-wide mt-4">
                Let's get you back in.
              </h1>
              <p className="mt-3 text-black/70">
                Pop your email below and we'll send you a link to set a new password.
              </p>
              <form onSubmit={requestReset} className="mt-6 space-y-4">
                <div>
                  <label className="block text-xs font-semibold mb-1">Email</label>
                  <input
                    type="email"
                    className="input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                  />
                </div>
                <button className="btn-primary w-full justify-center" disabled={busy} data-testid="forgot-submit-btn">
                  Send reset link <ArrowRight size={16} weight="bold" />
                </button>
              </form>
              {linkSent && (
                <div className="ai-insight mt-6 text-sm">
                  If that email is registered, a reset link has been sent. Please check your inbox
                  and spam folder.
                  {devLink && (
                    <div className="mt-3 text-xs">
                      <strong>Dev link (SMTP not yet wired):</strong>{" "}
                      <a href={devLink} className="underline font-semibold break-all">
                        {window.location.origin}
                        {devLink}
                      </a>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>
      <footer className="border-t border-black/15 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center gap-3">
          <img src={PROFITMIND_LOGO} alt="ProfitMIND" className="h-10 w-auto rounded-lg" />
          <div className="text-xs text-black/60">
            forecastly is a brand of{" "}
            <a
              href="https://profitmind.co.uk/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
            >
              ProfitMIND LTD
            </a>
            . © {new Date().getFullYear()} ProfitMIND. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
